import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import { toast } from 'sonner';

// Categorias de POIs externos via OpenStreetMap Overpass (grátis, sem API key)
export const EXTERNAL_POI_CATEGORIES = {
  fuel: { label: 'Combustível', emoji: '⛽', selector: '["amenity"="fuel"]' },
  lodging: { label: 'Dormidas', emoji: '🏨', selector: '["tourism"~"^(hotel|guest_house|hostel|motel|camp_site|apartment|chalet|alpine_hut)$"]' },
  food: { label: 'Comida', emoji: '🍴', selector: '["amenity"~"^(restaurant|cafe|fast_food)$"]' },
};

// Mirrors da Overpass API — tentamos em cascata se o primeiro falhar.
// Alguns ISPs / redes bloqueiam o principal; os mirrors "kumi" e "coffee"
// costumam funcionar quando o `.de` está indisponível.
const OVERPASS_MIRRORS = [
  'https://overpass-api.de/api/interpreter',
  'https://overpass.kumi.systems/api/interpreter',
  'https://overpass.private.coffee/api/interpreter',
];

const catIcon = (emoji) =>
  L.divIcon({
    className: 'tectur-ext-poi',
    html: `<div style="width:30px;height:30px;background:#fff;border:2px solid #334155;border-radius:50%;box-shadow:0 2px 6px rgba(0,0,0,.35);display:flex;align-items:center;justify-content:center;font-size:16px;">${emoji}</div>`,
    iconSize: [30, 30],
    iconAnchor: [15, 15],
    popupAnchor: [0, -15],
  });

const ICONS = Object.fromEntries(
  Object.entries(EXTERNAL_POI_CATEGORIES).map(([k, c]) => [k, catIcon(c.emoji)]),
);

// Tenta vários mirrors até um responder. Devolve JSON ou null.
async function fetchOverpassWithFallback(query) {
  const body = `data=${encodeURIComponent(query)}`;
  let lastErr = null;
  for (const url of OVERPASS_MIRRORS) {
    try {
      const r = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body,
      });
      if (!r.ok) {
        lastErr = new Error(`HTTP ${r.status}`);
        continue;
      }
      const json = await r.json();
      return json;
    } catch (e) {
      lastErr = e;
      continue;
    }
  }
  throw lastErr || new Error('Todos os mirrors Overpass falharam');
}

const ExternalPoisLayer = ({ bounds, enabledCats }) => {
  const [data, setData] = useState({}); // cat -> [{id,lat,lon,name,tags}]
  const fetchedRef = useRef({});

  const bbox = useMemo(() => {
    if (!bounds || bounds.length === 0) return null;
    let s = 90, n = -90, w = 180, e = -180;
    bounds.forEach(([lat, lon]) => {
      if (lat < s) s = lat;
      if (lat > n) n = lat;
      if (lon < w) w = lon;
      if (lon > e) e = lon;
    });
    const pad = 0.05;
    return `${s - pad},${w - pad},${n + pad},${e + pad}`;
  }, [bounds]);

  // Reset da cache quando a rota (bbox) muda — DEVE correr antes do fetch.
  useEffect(() => {
    fetchedRef.current = {};
    setData({});
  }, [bbox]);

  useEffect(() => {
    if (!bbox) return;
    Object.keys(enabledCats).forEach((key) => {
      if (!enabledCats[key] || fetchedRef.current[key]) return;
      fetchedRef.current[key] = true;
      const catLabel = EXTERNAL_POI_CATEGORIES[key].label;
      const sel = EXTERNAL_POI_CATEGORIES[key].selector;
      // Timeout 15s + limite 100 para respostas mais rápidas
      const q = `[out:json][timeout:15];(node${sel}(${bbox});way${sel}(${bbox}););out center 100;`;
      const toastId = toast.loading(`A procurar ${catLabel}...`);
      fetchOverpassWithFallback(q)
        .then((json) => {
          const items = (json.elements || [])
            .map((el) => ({
              id: `${key}-${el.id}`,
              lat: el.lat ?? el.center?.lat,
              lon: el.lon ?? el.center?.lon,
              name: el.tags?.name || EXTERNAL_POI_CATEGORIES[key].label,
              tags: el.tags || {},
            }))
            .filter((p) => p.lat != null && p.lon != null);
          setData((prev) => ({ ...prev, [key]: items }));
          if (items.length === 0) {
            toast.info(`Sem ${catLabel.toLowerCase()} nesta zona`, { id: toastId });
          } else {
            toast.success(`${items.length} ${catLabel.toLowerCase()} encontrados`, { id: toastId });
          }
        })
        .catch((err) => {
          console.error(`[ExternalPois] fetch falhou para ${key}:`, err);
          toast.error(`Não foi possível carregar ${catLabel.toLowerCase()}. Tenta de novo.`, { id: toastId });
          // permite retry no próximo toggle
          fetchedRef.current[key] = false;
        });
    });
  }, [enabledCats, bbox]);

  return (
    <>
      {Object.keys(EXTERNAL_POI_CATEGORIES).map((key) =>
        enabledCats[key]
          ? (data[key] || []).map((p) => (
              <Marker key={p.id} position={[p.lat, p.lon]} icon={ICONS[key]}>
                <Popup>
                  <div className="text-sm" style={{ minWidth: 160 }}>
                    <div className="font-bold text-base mb-1">
                      {EXTERNAL_POI_CATEGORIES[key].emoji} {p.name}
                    </div>
                    {p.tags['addr:street'] && (
                      <div className="text-xs text-slate-600">
                        {p.tags['addr:street']} {p.tags['addr:housenumber'] || ''}
                      </div>
                    )}
                    {p.tags.opening_hours && (
                      <div className="text-xs text-slate-600 mt-0.5">🕒 {p.tags.opening_hours}</div>
                    )}
                    {p.tags.phone && (
                      <div className="text-xs text-slate-600 mt-0.5">📞 {p.tags.phone}</div>
                    )}
                    <a
                      href={`https://www.google.com/maps/dir/?api=1&destination=${p.lat},${p.lon}`}
                      target="_blank"
                      rel="noreferrer"
                      className="text-xs text-blue-600 underline block mt-1.5"
                    >
                      Direções no Google Maps →
                    </a>
                  </div>
                </Popup>
              </Marker>
            ))
          : null,
      )}
    </>
  );
};

export default ExternalPoisLayer;
